import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { sessionAPI } from '../../services/api';
import './JitsiMeet.css';

// ICE servers for cross-network audio/video (meet.themindmate.app). Reduces BridgeChannel / ICE restart issues.
const JITSI_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:freeturn.net:3478' },
  { urls: 'turn:freeturn.net:3478', username: 'free', credential: 'free' },
  { urls: 'turn:freeturn.net:5349', username: 'free', credential: 'free' }
];

const MeetingRoom = ({
  sessionId,
  initialMeetingAccess,
  initialJitsi,
  meetingTitle,
  meetingSubject,
  meetingTeacherName,
  appName: appNameProp,
  onLeave,
  isTeacher: isTeacherProp = false
}) => {
  const meetingAccess = initialMeetingAccess || initialJitsi;
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const hashIframeRef = useRef(null);
  const hasRedirectedRef = useRef(false);
  const [containerReady, setContainerReady] = useState(false);
  const setJitsiRef = useCallback((node) => {
    jitsiContainerRef.current = node;
    setContainerReady(!!node);
  }, []);
  // Store Jitsi API in ref to avoid re-renders and cleanup loops
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [meetingProvider, setMeetingProvider] = useState(null);
  const [roomNameState, setRoomNameState] = useState(null);
  const [meetDomain, setMeetDomain] = useState(null);
  const [joinUrlState, setJoinUrlState] = useState(null);
  const [jwtTokenState, setJwtTokenState] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { user } = useAuth();
  // Only teacher and admin can end meeting for all; parent and student must never see the button or have moderator tools
  const isTeacher = isTeacherProp && user?.role !== 'student' && user?.role !== 'parent';

  useEffect(() => {
    return () => {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
        jitsiApiRef.current = null;
      }
      if (hashIframeRef.current?.parentNode) {
        hashIframeRef.current.parentNode.removeChild(hashIframeRef.current);
        hashIframeRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const hasJitsiSource = sessionId || (meetingAccess && meetingAccess.domain && meetingAccess.roomName);
    if (!error && !isInitialized && hasJitsiSource && containerReady && !jitsiApiRef.current) {
      const initializeJitsi = async () => {
        try {
          setLoading(true);
          setIsInitialized(true);

          let provider, domain, roomName, token, config, joinUrl;
          if (meetingAccess && meetingAccess.domain && meetingAccess.roomName) {
            provider = meetingAccess.provider || null;
            domain = meetingAccess.domain;
            roomName = meetingAccess.roomName;
            token = (meetingAccess.token != null && meetingAccess.token !== '') ? String(meetingAccess.token) : null;
            config = meetingAccess.config || {};
            joinUrl = meetingAccess.joinUrl || null;
            if (meetingTitle) setSessionData({ title: meetingTitle });
          } else {
            const response = await sessionAPI.joinSession(sessionId);
            const payload = response.data?.data || response.data;
            const liveMeetingAccess = payload?.meetingAccess || payload?.jitsi;
            if (!liveMeetingAccess) throw new Error('Invalid session data received');
            setSessionData(payload.session);
            provider = liveMeetingAccess.provider || null;
            domain = liveMeetingAccess.domain;
            roomName = liveMeetingAccess.roomName;
            token = (liveMeetingAccess.token != null && liveMeetingAccess.token !== '') ? String(liveMeetingAccess.token) : null;
            config = liveMeetingAccess.config || {};
            joinUrl = liveMeetingAccess.joinUrl || null;
          }

          setMeetingProvider(provider || null);
          setRoomNameState(roomName);
          setMeetDomain(domain || null);
          setJoinUrlState(joinUrl || null);
          setJwtTokenState(token || null);

          if (!jitsiContainerRef.current) throw new Error('Jitsi container element not found');

          // Bunch docs require redirecting to joinUrl instead of embedding it in an iframe.
          if (provider === 'bunch' && joinUrl) {
            if (!hasRedirectedRef.current) {
              hasRedirectedRef.current = true;
              window.location.assign(joinUrl);
            }
            return;
          }

          // 400 fix: use #jwt= only (never ?jwt=). When we have a token, embed with hash URL and do NOT load external_api.js.
          const hasToken = !!token;
          if (hasToken && domain && roomName) {
            const meetingUrl = `https://${domain}/${roomName}#jwt=${encodeURIComponent(token)}`;
            const iframe = document.createElement('iframe');
            iframe.src = meetingUrl;
            iframe.allow = 'camera; microphone; fullscreen; display-capture; autoplay';
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.border = 'none';
            iframe.setAttribute('data-jitsi-url', meetingUrl);
            jitsiContainerRef.current.appendChild(iframe);
            hashIframeRef.current = iframe;
            setLoading(false);
            return;
          }

          if (!window.JitsiMeetExternalAPI) {
            const loadScript = (src) => new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = src;
              script.async = true;
              script.crossOrigin = 'anonymous';
              script.onload = () => resolve();
              script.onerror = (e) => reject(e);
              document.head.appendChild(script);
            });
            try {
              await loadScript('https://meet.jit.si/external_api.js');
            } catch (e) {
              await loadScript('https://meet.jit.si/external_api.js').catch(() => { throw e; });
            }
          }

          const { featureFlags: _strip, ...safeConfig } = config || {};
          const options = {
            roomName,
            jwt: token || undefined,
            width: '100%',
            height: '100%',
            parentNode: jitsiContainerRef.current,
            configOverwrite: {
              ...safeConfig,
              startWithAudioMuted: !isTeacher,
              startWithVideoMuted: false,
              enableWelcomePage: false,
              prejoinPageEnabled: false,
              disableRecordAudioNotification: true,
              enableUserRolesBasedOnToken: true,
              enableFeaturesBasedOnToken: true,
              resolution: 720,
              constraints: {
                video: {
                  aspectRatio: 16 / 9,
                  height: {
                    ideal: 720,
                    max: 720,
                    min: 240
                  }
                }
              },
              disableAudioLevels: false,
              enableNoAudioDetection: true,
              enableNoisyMicDetection: true,
              channelLastN: 20,
              enableLayerSuspension: true,
              peerConnectionConfig: {
                iceServers: JITSI_ICE_SERVERS
              },
              p2p: {
                enabled: true,
                stunServers: JITSI_ICE_SERVERS
              }
            },
            toolbarButtons: [
              'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
              'fodeviceselection', 'hangup', 'profile', 'info', 'chat', 'recording',
              'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
              'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
              'tileview', 'videobackgroundblur', 'download', 'help'
            ].filter(button => {
              // Remove certain buttons for students
              if (!isTeacher && ['recording', 'livestreaming', 'invite'].includes(button)) {
                return false;
              }
              return true;
            }),
            interfaceConfigOverwrite: {
              SHOW_JITSI_WATERMARK: false,
              SHOW_WATERMARK_FOR_GUESTS: false,
              SHOW_BRAND_WATERMARK: false,
              BRAND_WATERMARK_LINK: '',
              SHOW_POWERED_BY: false,
              DISPLAY_WELCOME_PAGE_CONTENT: false,
              DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT: false,
              APP_NAME: appNameProp ? `${appNameProp} Meeting` : 'PassAm Meeting',
              NATIVE_APP_NAME: appNameProp || 'PassAm',
              PROVIDER_NAME: appNameProp || 'PassAm',
              LANG_DETECTION: true,
              CONNECTION_INDICATOR_AUTO_HIDE_ENABLED: true,
              CONNECTION_INDICATOR_AUTO_HIDE_TIMEOUT: 5000,
              MAXIMUM_ZOOMING_COEFFICIENT: 1.3,
              FILM_STRIP_MAX_HEIGHT: 120
            },
            userInfo: {
              displayName: [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.fullName || user?.name || user?.email || 'Participant',
              email: user?.email
            }
          };

          const api = new window.JitsiMeetExternalAPI(domain, options);
          jitsiApiRef.current = api;
          // Hide overlay once the interface is rendered (prejoin visible)
          setLoading(false);

          // Event listeners
          api.addEventListener('readyToClose', () => {
            if (typeof onLeave === 'function') onLeave();
          });

          api.addEventListener('participantJoined', () => {});

          api.addEventListener('participantLeft', () => {});

          api.addEventListener('videoConferenceJoined', () => {
            setLoading(false);
          });

          api.addEventListener('videoConferenceLeft', () => {});

          api.addEventListener('errorOccurred', (ev) => {
            const { type, name, message, isFatal } = ev || {};
            if (type === 'CONFERENCE' || type === 'CONNECTION' || isFatal) {
              console.error('🔍 JitsiMeet - errorOccurred:', { type, name, message, isFatal });
              const reason = (message || name || 'Conference connection failed.').toLowerCase();
              const isTimeout = reason.includes('timeout') || (name && String(name).toLowerCase().includes('timeout'));
              setError(isTimeout
                ? 'Connection timed out. The meeting server may be busy or unreachable. Try again or open in a new tab.'
                : (message || 'Conference failed. Try again or open in a new tab.'));
              setLoading(false);
            }
          });

          const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.fullName || user?.name || user?.email || 'Participant';
          api.executeCommand('displayName', displayName);

        } catch (err) {
          console.error('MeetingRoom - error loading meeting UI:', err);
          console.error('MeetingRoom - error stack:', err?.stack);
          setError('Embed blocked or API load failed. Use “Open in new tab”.');
          setLoading(false);
          setIsInitialized(false);

          if (sessionId) {
            try {
              const fallback = await sessionAPI.getSession(sessionId);
              const sess = fallback.data?.data?.session || fallback.data?.session;
              const room = sess?.jitsiConfig?.roomName || sess?.jitsi?.roomName;
              if (room) setRoomNameState(room);
            } catch {}
          } else if (meetingAccess?.roomName) {
            setRoomNameState(meetingAccess.roomName);
          }
        }
      };

      initializeJitsi();
    }
  }, [error, isInitialized, sessionId, meetingAccess, meetingTitle, user, isTeacher, onLeave, containerReady, appNameProp]);

  const openMeetingInNewTab = () => {
    if (!openInNewTabUrl) return;
    window.open(openInNewTabUrl, '_blank', 'noopener,noreferrer');
  };

  const endSession = async () => {
    let endError = null;
    try {
      if (sessionId) {
        try { await sessionAPI.joinSession(sessionId); } catch (_) {}
      }
      if (isTeacher && sessionId) {
        await sessionAPI.endSession(sessionId);
      }
    } catch (err) {
      endError = err;
      console.error('Error ending session:', err);
    }

    try {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.executeCommand('hangup');
      }
      if (hashIframeRef.current?.parentNode) {
        hashIframeRef.current.parentNode.removeChild(hashIframeRef.current);
        hashIframeRef.current = null;
      }
    } catch (hangupErr) {
      console.error('Error closing meeting UI:', hangupErr);
    }

    if (typeof onLeave === 'function') {
      onLeave();
      return;
    }

    if (endError) {
      setError('Failed to end session cleanly. Try opening the meeting in a new tab and closing it there.');
    }
  };

  const openInNewTabUrl = joinUrlState || (roomNameState
    ? (meetDomain ? `https://${meetDomain}/${roomNameState}` : `https://meet.jit.si/${roomNameState}`) +
      (jwtTokenState ? `#jwt=${encodeURIComponent(jwtTokenState)}` : '')
    : null);

  if (error) {
    return (
      <div className="jitsi-error">
        <h3>Connection Error</h3>
        <p>{error}</p>
        {openInNewTabUrl && (
          <button onClick={openMeetingInNewTab} className="retry-btn" type="button">
            Open in new tab
          </button>
        )}
        <button onClick={() => window.location.reload()} className="retry-btn" type="button">
          Retry
        </button>
      </div>
    );
  }

  const displayTitle = meetingTitle || sessionData?.title;
  const showHeader = displayTitle || meetingSubject || meetingTeacherName || (sessionData && (sessionData.subject || sessionData.teacher));

  if (meetingProvider === 'bunch' && joinUrlState) {
    return (
      <div className="jitsi-meet-container">
        <div className="jitsi-loading">
          <div className="loading-spinner"></div>
          <p>Opening meeting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="jitsi-meet-container">
      {loading && (
        <div className="jitsi-loading">
          <div className="loading-spinner"></div>
          <p>Joining session...</p>
        </div>
      )}
      {showHeader && (
        <div className="session-header">
          {displayTitle && <h3>{displayTitle}</h3>}
          <div className="session-info session-info--right">
            {meetingSubject && <span className="session-info-subject">Subject: {meetingSubject}</span>}
            {meetingTeacherName && <span className="session-info-teacher">Teacher: {meetingTeacherName}</span>}
            {sessionData?.subject && !meetingSubject && <span>Subject: {sessionData.subject}</span>}
            {sessionData?.teacher?.name && !meetingTeacherName && <span>Teacher: {sessionData.teacher.name}</span>}
            {sessionData?.teacher && !sessionData?.teacher?.name && !meetingTeacherName && typeof sessionData.teacher === 'string' && <span>Teacher: {sessionData.teacher}</span>}
            {openInNewTabUrl && (
              <button onClick={openMeetingInNewTab} className="end-session-btn" type="button">
                Open in new tab
              </button>
            )}
            {isTeacher && (
              <button onClick={endSession} className="end-session-btn" type="button">
                End Session
              </button>
            )}
          </div>
        </div>
      )}
      <div ref={setJitsiRef} className="jitsi-container" />
    </div>
  );
};

export default MeetingRoom;